import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Tooltip } from '@mui/material';

// Extended interfaces for multi-select and grouping
export interface ColumnMappingGroup {
  id: string;
  mappings: MultiSelectMapping[];
  operator: 'AND' | 'OR';
}

export interface MultiSelectMapping {
  id: string;
  leftColumns: string[];
  rightColumns: string[];
}

interface ColumnMappingGroupsProps {
  smartMatch: any;
  leftColumns: string[];
  rightColumns: string[];
  onMappingsChange?: (groups: ColumnMappingGroup[]) => void;
  externalMappingGroups?: ColumnMappingGroup[];
}

const ColumnMappingGroups: React.FC<ColumnMappingGroupsProps> = ({
  smartMatch,
  leftColumns,
  rightColumns,
  onMappingsChange,
  externalMappingGroups
}) => {
  const [mappingGroups, setMappingGroups] = useState<ColumnMappingGroup[]>([
    {
      id: `group_${Date.now()}`,
      mappings: [],
      operator: 'AND'
    }
  ]);

  const [selectedLeftColumn, setSelectedLeftColumn] = useState<string>('');
  const [selectedRightColumn, setSelectedRightColumn] = useState<string>('');
  const [openOperatorDropdown, setOpenOperatorDropdown] = useState<string | null>(null); // Track which mapping has dropdown open
  const [isLeftDropdownOpen, setIsLeftDropdownOpen] = useState<boolean>(false);
  const [isRightDropdownOpen, setIsRightDropdownOpen] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>(''); // Validation message for Add Mapping button
  
  // State for editing pills
  const [editingPill, setEditingPill] = useState<{
    groupId: string;
    mappingId: string;
    side: 'left' | 'right';
    columnIndex: number;
    position: { top: number; left: number; width: number };
  } | null>(null);
  
  const hasInitialized = useRef(false);
  const mappingMetadata = useRef<Map<string, { groupId: string; mappingId: string }>>(new Map());
  const pillRefs = useRef<Map<string, HTMLElement>>(new Map());
  const operatorButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown-container]') && 
          !target.closest('[data-pill-dropdown]') && 
          !target.closest('[data-operator-dropdown]')) {
        setOpenOperatorDropdown(null);
        setIsLeftDropdownOpen(false);
        setIsRightDropdownOpen(false);
        setEditingPill(null);
      }
    };

    if (openOperatorDropdown || isLeftDropdownOpen || isRightDropdownOpen || editingPill) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    
    return undefined;
  }, [openOperatorDropdown, isLeftDropdownOpen, isRightDropdownOpen, editingPill]);

  // Initialize mappings on component mount if there are existing mappings
  // This runs whenever the component mounts, including when navigating back
  useEffect(() => {
    // Reset initialization flag when component mounts to allow restoration
    // This ensures state persists when navigating back and forth
    if (smartMatch.state.mappingGroups.length > 0) {
      // Use stored mapping groups structure
      setMappingGroups(smartMatch.state.mappingGroups);
      hasInitialized.current = true;
      console.log(`🔄 Restored ${smartMatch.state.mappingGroups.length} mapping groups from smartMatch state`);
    } else if (smartMatch.state.columnMappings.length > 0 && !hasInitialized.current) {
      // Fallback: Create individual mappings for each column pair (for backward compatibility)
      const individualMappings: MultiSelectMapping[] = smartMatch.state.columnMappings.map((mapping: any) => ({
        id: mapping.id,
        leftColumns: [mapping.left],
        rightColumns: [mapping.right]
      }));

      const convertedGroups: ColumnMappingGroup[] = [{
        id: `group_${Date.now()}`,
        mappings: individualMappings,
        operator: 'AND'
      }];
      
      setMappingGroups(convertedGroups);
      hasInitialized.current = true;
      console.log(`🔄 Initialized ${individualMappings.length} mappings on component mount (fallback)`);
    }
  }, []); // Run only on mount - refs reset on unmount, so this will restore state when navigating back

  // Handle external mapping groups (e.g., from AI generation)
  useEffect(() => {
    if (externalMappingGroups && externalMappingGroups.length > 0) {
      console.log("🤖 Applying external mapping groups:", externalMappingGroups);
      setMappingGroups(externalMappingGroups);
      hasInitialized.current = true;
    }
  }, [externalMappingGroups]);

  // FIXED: Reconstruct multi-select mappings from flat mappings on mount
  useEffect(() => {
    if (hasInitialized.current || smartMatch.state.columnMappings.length === 0) {
      return;
    }

    // Create individual mappings for each column pair
    // Since we don't have reliable metadata after remount, treat each as individual mapping
    const individualMappings: MultiSelectMapping[] = smartMatch.state.columnMappings.map((mapping: any) => ({
      id: mapping.id,
      leftColumns: [mapping.left],
      rightColumns: [mapping.right]
    }));

    if (individualMappings.length > 0) {
      const convertedGroups: ColumnMappingGroup[] = [{
        id: `group_${Date.now()}`,
        mappings: individualMappings,
        operator: 'AND'
      }];
      
      setMappingGroups(convertedGroups);
      hasInitialized.current = true;
      console.log(`🔄 Reconstructed ${individualMappings.length} mappings from smartMatch state`);
    }
  }, [smartMatch.state.columnMappings]);

  // FIXED: Sync as a GROUP, not individual pairs
  useEffect(() => {
    if (!hasInitialized.current && smartMatch.state.columnMappings.length > 0) {
      return;
    }

    const flatMappings: any[] = [];
    const newMetadata = new Map<string, { groupId: string; mappingId: string }>();
    
    mappingGroups.forEach(group => {
      group.mappings.forEach(mapping => {
        const groupKey = `${group.id}_${mapping.id}`;
        
        mapping.leftColumns.forEach(leftCol => {
          mapping.rightColumns.forEach(rightCol => {
            const flatMappingId = `${mapping.id}_${leftCol}_${rightCol}`;
            
            flatMappings.push({
              id: flatMappingId,
              left: leftCol,
              right: rightCol
            });
            
            newMetadata.set(flatMappingId, {
              groupId: group.id,
              mappingId: mapping.id
            });
          });
        });
      });
    });

    mappingMetadata.current = newMetadata;

    smartMatch.clearColumnMappings();
    flatMappings.forEach(mapping => {
      smartMatch.addColumnMapping(mapping.left, mapping.right);
    });

    // Save mapping groups structure to smartMatch state
    smartMatch.setMappingGroups(mappingGroups);

    onMappingsChange?.(mappingGroups);
  }, [mappingGroups]);

  // Handle Add Mapping button click
  const handleAddMapping = () => {
    // Clear validation message
    setValidationMessage('');

    // Validate that both columns are selected
    if (!selectedLeftColumn && !selectedRightColumn) {
      setValidationMessage('Please select columns from both tables');
      return;
    }
    if (!selectedLeftColumn) {
      setValidationMessage('Please select a column from the left table');
      return;
    }
    if (!selectedRightColumn) {
      setValidationMessage('Please select a column from the right table');
      return;
    }

    // Create new mapping
    const newMapping: MultiSelectMapping = {
      id: `mapping_${Date.now()}_${Math.random()}`,
      leftColumns: [selectedLeftColumn],
      rightColumns: [selectedRightColumn]
    };

    setMappingGroups(prev => {
      const updated = [...prev];
      const lastGroupIndex = updated.length - 1;
      updated[lastGroupIndex] = {
        ...updated[lastGroupIndex],
        mappings: [...updated[lastGroupIndex].mappings, newMapping]
      };
      return updated;
    });

    // Clear selections after adding
    setSelectedLeftColumn('');
    setSelectedRightColumn('');
    hasInitialized.current = true;
  };

  const handleRemoveMapping = (groupId: string, mappingId: string) => {
    setMappingGroups(prev => 
      prev.map(group => 
        group.id === groupId 
          ? { ...group, mappings: group.mappings.filter(m => m.id !== mappingId) }
          : group
      ).filter(group => group.mappings.length > 0 || group.id === prev[prev.length - 1].id)
    );
  };

  const handleOperatorChange = (groupId: string, operator: 'AND' | 'OR') => {
    setMappingGroups(prev => {
      const updated = prev.map(group => 
        group.id === groupId ? { ...group, operator } : group
      );

      if (operator === 'OR') {
        const groupIndex = updated.findIndex(g => g.id === groupId);
        if (groupIndex !== -1 && groupIndex === updated.length - 1) {
          updated.push({
            id: `group_${Date.now()}`,
            mappings: [],
            operator: 'AND'
          });
        }
      }

      return updated;
    });
    setOpenOperatorDropdown(null);
  };

  const handleColumnSelect = (side: 'left' | 'right', column: string) => {
    // Clear validation message when user selects a column
    setValidationMessage('');

    if (side === 'left') {
      setSelectedLeftColumn(column);
      setIsLeftDropdownOpen(false);
    } else {
      setSelectedRightColumn(column);
      setIsRightDropdownOpen(false);
    }
  };

  // Handle pill click to open edit dropdown
  const handlePillClick = (
    event: React.MouseEvent<HTMLSpanElement>,
    groupId: string,
    mappingId: string,
    side: 'left' | 'right',
    columnIndex: number
  ) => {
    event.stopPropagation();
    const pillKey = `${groupId}-${mappingId}-${side}`;
    const containerElement = pillRefs.current.get(pillKey);
    
    if (containerElement) {
      const rect = containerElement.getBoundingClientRect();
      setEditingPill({
        groupId,
        mappingId,
        side,
        columnIndex,
        position: {
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        }
      });
    }
  };

  // Handle editing a column in a pill
  const handleEditColumn = (newColumn: string) => {
    if (!editingPill) return;

    setMappingGroups(prev => 
      prev.map(group => {
        if (group.id === editingPill.groupId) {
          return {
            ...group,
            mappings: group.mappings.map(mapping => {
              if (mapping.id === editingPill.mappingId) {
                if (editingPill.side === 'left') {
                  const newLeftColumns = [...mapping.leftColumns];
                  newLeftColumns[editingPill.columnIndex] = newColumn;
                  return { ...mapping, leftColumns: newLeftColumns };
                } else {
                  const newRightColumns = [...mapping.rightColumns];
                  newRightColumns[editingPill.columnIndex] = newColumn;
                  return { ...mapping, rightColumns: newRightColumns };
                }
              }
              return mapping;
            })
          };
        }
        return group;
      })
    );

    setEditingPill(null);
  };

  // Handle removing a single column from a mapping
  const handleRemoveColumn = (groupId: string, mappingId: string, side: 'left' | 'right', columnIndex: number) => {
    setMappingGroups(prev => 
      prev.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            mappings: group.mappings.map(mapping => {
              if (mapping.id === mappingId) {
                if (side === 'left') {
                  const newLeftColumns = mapping.leftColumns.filter((_, i) => i !== columnIndex);
                  // If no columns left, remove the entire mapping
                  if (newLeftColumns.length === 0) return null;
                  return { ...mapping, leftColumns: newLeftColumns };
                } else {
                  const newRightColumns = mapping.rightColumns.filter((_, i) => i !== columnIndex);
                  // If no columns left, remove the entire mapping
                  if (newRightColumns.length === 0) return null;
                  return { ...mapping, rightColumns: newRightColumns };
                }
              }
              return mapping;
            }).filter(Boolean) as MultiSelectMapping[]
          };
        }
        return group;
      }).filter(group => group.mappings.length > 0 || group.id === prev[prev.length - 1].id)
    );
  };

  const renderDropdown = (
    title: string, 
    columns: string[], 
    selectedColumn: string, 
    side: 'left' | 'right',
    isOpen: boolean,
    setIsOpen: (open: boolean) => void
  ) => (
    <div className="flex-1 min-w-0">
      <h4 className="text-[12px] font-semibold text-gray-800 m-0 mb-1.5">
        {title}
      </h4>
      <div className="relative" data-dropdown-container>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 text-[12px] font-medium border border-gray-100 rounded-lg text-left cursor-pointer flex items-center justify-between transition-all duration-200 hover:border-gray-200"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
        >
          <span className={selectedColumn ? 'text-gray-800' : 'text-gray-400'}>
            {selectedColumn || 'Select column'}
          </span>
          <svg 
            width="12" 
            height="12" 
            viewBox="0 0 12 12" 
            fill="none"
            className={`transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] max-h-[250px] overflow-y-auto scrollable-dropdown">
            {columns.map((column) => (
              <button
                key={column}
                onClick={() => handleColumnSelect(side, column)}
                className={`w-full p-2 px-3 text-[12px] text-left border-none cursor-pointer transition-colors duration-200 ${
                  selectedColumn === column
                    ? 'bg-blue-50 text-blue-500 font-semibold'
                    : 'bg-transparent text-gray-700 hover:bg-gray-50 font-normal'
                }`}
              >
                {column}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderEditablePill = (
    col: string,
    groupId: string,
    mappingId: string,
    side: 'left' | 'right',
    columnIndex: number,
    _totalColumns: number
  ) => {
    const pillKey = `${groupId}-${mappingId}-${side}`;
    
    return (
      <Tooltip key={`${col}-${columnIndex}`} title={col} arrow placement="top">
        <span 
          ref={(el) => {
            if (el) pillRefs.current.set(pillKey, el.parentElement as HTMLElement);
          }}
          className="text-gray-700 text-[12px] font-normal cursor-pointer hover:text-gray-900 truncate"
          onClick={(e) => handlePillClick(e, groupId, mappingId, side, columnIndex)}
        >
          {col}
        </span>
      </Tooltip>
    );
  };

  const renderMappingGroup = (group: ColumnMappingGroup, groupIndex: number) => (
    <div key={group.id} className="mb-3">
      {/* OR separator between groups */}
      {groupIndex > 0 && (
        <div className="flex items-center justify-center mb-3" style={{ marginRight: '52px' }}>
          <div className="px-2 py-1.5 text-[11px] font-semibold" style={{ backgroundColor: '#FFE1F6', color: '#FA04FE', borderRadius: '4px' }}>
            OR
          </div>
        </div>
      )}

      <div 
        className="bg-mint-light rounded-xl p-3"
      >
        {group.mappings.map((mapping, mappingIndex) => (
          <div 
            key={mapping.id} 
            className={mappingIndex < group.mappings.length - 1 ? "mb-3" : ""}
          >
            {/* AND separator between mappings in same group */}
            {mappingIndex > 0 && (
              <div className="flex items-center justify-center mb-3" style={{ marginRight: '52px' }}>
                <div className="px-2 py-1.5 text-[11px] font-semibold" style={{ backgroundColor: '#E1ECFF', color: '#0461FE', borderRadius: '4px' }}>
                  AND
                </div>
              </div>
            )}
            
            <div className="mapping-row flex items-center gap-2">
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <div className="rounded-lg px-3 py-2 min-h-[36px] flex gap-2 items-center overflow-x-auto overflow-y-hidden pill-container bg-white">
                  {mapping.leftColumns.map((col, idx) => 
                    renderEditablePill(col, group.id, mapping.id, 'left', idx, mapping.leftColumns.length)
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '28px', minHeight: '36px' }}>
                <img 
                  src="assets/iconsax-link.png" 
                  alt="Linked" 
                  width="24" 
                  height="24" 
                  className="object-contain"
                />
              </div>

              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <div className="rounded-lg px-3 py-2 min-h-[36px] flex gap-2 items-center overflow-x-auto overflow-y-hidden pill-container bg-white">
                  {mapping.rightColumns.map((col, idx) => 
                    renderEditablePill(col, group.id, mapping.id, 'right', idx, mapping.rightColumns.length)
                  )}
                </div>
              </div>

              {/* Action buttons - only visible on hover */}
              <div className="mapping-actions flex gap-1 items-center justify-end flex-shrink-0" style={{ width: '48px' }}>
                <button
                  type="button"
                  aria-label="Remove mapping"
                  onClick={() => handleRemoveMapping(group.id, mapping.id)}
                  className="w-5 h-5 rounded-full bg-red-400 hover:bg-red-500 transition-colors duration-200 cursor-pointer inline-flex items-center justify-center border-none p-0 m-0"
                >
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                
                {/* Only show + button on the last mapping in the group */}
                {mappingIndex === group.mappings.length - 1 && (
                  <div className="relative" data-operator-dropdown>
                    <button
                      type="button"
                      aria-label="Add mapping"
                      ref={operatorButtonRef}
                      onClick={() => {
                        const mappingKey = `${group.id}-${mapping.id}`;
                        setOpenOperatorDropdown(openOperatorDropdown === mappingKey ? null : mappingKey);
                      }}
                      className="w-5 h-5 rounded-full bg-teal-500 hover:bg-teal-600 transition-colors duration-200 cursor-pointer inline-flex items-center justify-center border-none p-0 m-0"
                    >
                      <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3.5V12.5M3.5 8H12.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>

                    {openOperatorDropdown === `${group.id}-${mapping.id}` && (
                      <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-lg z-[1000] min-w-[80px] operator-dropdown-animate overflow-hidden border border-gray-100">
                        <button
                          onClick={() => handleOperatorChange(group.id, 'AND')}
                          className="w-full px-3 py-2 text-[12px] font-medium border-none cursor-pointer text-center transition-all duration-200 bg-white text-gray-700 hover:bg-gray-50"
                        >
                          And
                        </button>

                        <button
                          onClick={() => handleOperatorChange(group.id, 'OR')}
                          className="w-full px-3 py-2 text-[12px] font-medium border-none cursor-pointer text-center transition-all duration-200 bg-white text-gray-700 hover:bg-gray-50"
                        >
                          Or
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const currentOperator = mappingGroups[mappingGroups.length - 1].operator;

  return (
    <>
      <style>
        {`
          .bg-mint-light {
            background-color: rgba(255, 255, 255, 0.4);
          }
          
          .scrollable-dropdown {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          .scrollable-dropdown::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }

          .pill-container {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          .pill-container::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }

          @keyframes scaleIn {
            0% {
              opacity: 0;
              transform: scale(0.95);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          .operator-dropdown-animate {
            animation: scaleIn 0.15s ease-out;
          }

          .mapping-row:hover .mapping-actions {
            opacity: 1;
          }

          .mapping-actions {
            opacity: 0;
            transition: opacity 0.2s ease;
          }
        `}
      </style>
      <div>
        <div 
          className="rounded-lg p-4 shadow-sm mb-5"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-800 m-0">
              Column Mapping
            </h3>
          </div>
          <p className="text-xs text-gray-500 m-0 mb-4">
            Map columns between your tables
          </p>

          {/* Mappings Display Area */}
          {mappingGroups.some(group => group.mappings.length > 0) && (
            <div className="mb-4">
              {mappingGroups
                .filter(group => group.mappings.length > 0)
                .map((group, index) => renderMappingGroup(group, index))}
            </div>
          )}

          {/* Column Selection Area */}
          <div className="flex gap-3 items-end mb-4">
            {renderDropdown("Left table", leftColumns, selectedLeftColumn, 'left', isLeftDropdownOpen, setIsLeftDropdownOpen)}

            {renderDropdown("Right table", rightColumns, selectedRightColumn, 'right', isRightDropdownOpen, setIsRightDropdownOpen)}
          </div>

          {/* Validation Message */}
          {validationMessage && (
            <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200">
              <p className="text-xs text-red-600 m-0">{validationMessage}</p>
            </div>
          )}

          {/* Add Mapping Button */}
          <button
            onClick={handleAddMapping}
            className="w-full py-2.5 px-4 rounded-lg border-2 border-[#068E75] bg-transparent text-[#068E75] text-[13px] font-semibold cursor-pointer transition-all duration-200 hover:bg-[#068E75] hover:bg-opacity-5"
          >
            Add Mapping
          </button>
        </div>

        {/* Pill Edit Dropdown */}
        {editingPill && (
          <div 
            data-pill-dropdown
            className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-[2000] max-h-[200px] overflow-y-auto scrollable-dropdown"
            style={{
              top: `${editingPill.position.top + 4}px`,
              left: `${editingPill.position.left}px`,
              width: `${editingPill.position.width}px`
            }}
          >
            {(editingPill.side === 'left' ? leftColumns : rightColumns).map((column) => (
              <button
                key={column}
                onClick={() => handleEditColumn(column)}
                className={`w-full p-2 px-3 text-xs text-left border-none cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0 ${
                  (() => {
                    const group = mappingGroups.find(g => g.id === editingPill.groupId);
                    const mapping = group?.mappings.find(m => m.id === editingPill.mappingId);
                    const currentColumn = editingPill.side === 'left' 
                      ? mapping?.leftColumns[editingPill.columnIndex]
                      : mapping?.rightColumns[editingPill.columnIndex];
                    return currentColumn === column;
                  })()
                    ? 'bg-blue-50 text-blue-500 font-semibold'
                    : 'bg-transparent text-gray-700 hover:bg-gray-50 font-normal'
                }`}
              >
                {column}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ColumnMappingGroups;